import {
    ChangeDetectorRef, Component, OnDestroy, OnInit, 
    ChangeDetectionStrategy, HostListener, ElementRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationService}
    from '@app/secure/notification/notification.service';
import { Notification} from '@app/secure/model/Notification';
import { NotificationType} from '@app/secure/model/NotificationType';

@Component
({
    selector: 'app-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
class NotificationComponent implements OnInit, OnDestroy
{
    notifications: Notification[] = [];
    private subscription: Subscription;

    /**
     * @desc   - Wire notification stream and change detector.
     * @access - public
     * @param  - notificationSvc : toast notification service
     * @param  - cdr : change detector for deferred list updates
     * @param  - elementRef : reference to component host element
     * @return -
     */
    constructor
    (
        private notificationSvc: NotificationService,
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef
    )
    {
    }

    /**
     * @desc   - Apply a toast add/clear on the next microtask so the
     *           list does not mutate mid change-detection (NG0100).
     * @access - private
     * @param  - notification : toast payload from NotificationService
     * @return -
     */
    private addNotification
    (
        notification: Notification
    )
    {
        Promise.resolve().then(() =>
        {
            this.applyNotification(notification);
        });
    }

    /**
     * @desc   - Mutate the visible toast list and refresh the view.
     * @access - private
     * @param  - notification : toast payload from NotificationService
     * @return -
     */
    private applyNotification
    (
        notification: Notification
    )
    {
        if (notification.type === NotificationType.CLEAR)
        {
            this.notifications = [];
        }
        else if (notification.type === NotificationType.DISMISS_ONE)
        {
            this.notifications =
                this.notifications.filter(
                    notif => notif.id !== notification.id
                );
        }
        else
        {
            this.notifications = this.notifications.concat(
                [notification]
            );

            if (notification.timeout !== 0)
            {
                setTimeout(() => this.closeNotification(notification),
                    notification.timeout);
            }
        }

        this.cdr.markForCheck();
    }

    /**
     * @desc   - Subscribe to the shared notification stream.
     * @access - public
     * @param  -
     * @return -
     */
    ngOnInit(): void
    {
        this.subscription = this.notificationSvc.getObservable().
                            subscribe(notification=>
                                      this.addNotification(notification));
    }

    /**
     * @desc   - Drop the notification subscription.
     * @access - public
     * @param  -
     * @return -
     */
    ngOnDestroy()
    {
        this.subscription.unsubscribe();
    }

    /**
     * @desc   - Remove one toast on the next microtask (NG0100-safe).
     *           Local-only removal, used by auto-timeout.
     * @access - private
     * @param  - notification : toast to close
     * @return -
     */
    private closeLocal
    (
        notification
    )
    {
        Promise.resolve().then(() =>
        {
            this.notifications =
                this.notifications.filter(
                    notif => notif.id !== notification.id
                );
            this.cdr.markForCheck();
        });
    }

    /**
     * @desc   - Broadcast close to all component instances via the service.
     *           Called when user clicks a notification.
     * @access - public
     * @param  - notification : toast to close
     * @return -
     */
    closeNotification
    (
        notification
    )
    {
        this.notificationSvc.closeNotification(notification.id);
    }

    /**
     * @desc   - Handle document clicks to dismiss all toasts when clicking
     *           outside any notification.
     * @access - public
     * @param  - event : click event from document
     * @return -
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick
    (
        event: MouseEvent
    )
    {
        let clickedInside: boolean;
        let target: HTMLElement;

        if (!this.notifications.length)
        {
            return;
        }

        target = event.target as HTMLElement;
        clickedInside = this.elementRef.nativeElement.contains(target);

        if (!clickedInside)
        {
            this.notificationSvc.clear();
        }
    }

    /**
     * @desc   - Map notification type to CSS class name.
     * @access - public
     * @param  - notification : toast whose style is needed
     * @return - string CSS class for the toast type
     */
    className
    (
        notification: Notification
    ): string
    {
        let style: string;

        switch(notification.type)
        {
            case NotificationType.SUCCESS:
                style='success';
                break;
            case NotificationType.WARNING:
                style='warning';
                break;
            case NotificationType.ERROR:
                style='error';
                break;
            default:
                style='info';
                break;
            // end case
        }

        return style;
    }
}

export { NotificationComponent };
